/**
 * Weekday vocabulary shared by the booking calendar.
 *
 * Both API versions identify weekdays by these three-letter keys, and the
 * calendar grid works in `Date.getDay()` indices, so the conversion lives here
 * instead of being re-derived at each call site.
 *
 * Pure TypeScript - no React imports.
 */

/** Ordered so the array index matches `Date.prototype.getDay()`. */
export const WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

export type Weekday = (typeof WEEKDAYS)[number];

/** `Date.getDay()` index for a weekday key. */
export const weekdayIndex = (day: Weekday): number => WEEKDAYS.indexOf(day);

/** The weekday key a date falls on. */
export const weekdayFromDate = (date: Date): Weekday => WEEKDAYS[date.getDay()];

/** Narrows an untrusted API string to a weekday key. */
export const isWeekday = (value: unknown): value is Weekday =>
  typeof value === "string" && (WEEKDAYS as readonly string[]).includes(value);

/**
 * The weekdays not present in `days`.
 *
 * The v2 days endpoint reports the days *without* availability, so the calendar
 * needs the complement of what it returns.
 */
export const invertWeekdays = (days: readonly Weekday[]): Weekday[] =>
  WEEKDAYS.filter((day) => !days.includes(day));
